import { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material'
import { useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import { selectCourses } from '../../admin.slice'

type RestartCourseProps = {
  userID: string
}

export default ({ userID }: RestartCourseProps): ReactElement => {
  let [selectedCourse, setSelectedCourse] = useState<any>(null)
  let [loading, setLoading] = useState<boolean>(false)
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let courses = useAppSelector(selectCourses)

  let handleRestartCourse = async () => {
    if (!selectedCourse) return

    setLoading(true)
    try {
      await axios.post('/api/v2/admin/user/restart-course', {
        courseID: selectedCourse.id,
        userID,
      })
      setSuccessMessage('Course restarted successfully')
      setSelectedCourse(null)
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || 'Failed to restart course',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
        Restart Course
      </Typography>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}
      >
        <Autocomplete
          value={selectedCourse}
          onChange={(_, newValue) => setSelectedCourse(newValue)}
          options={courses}
          getOptionLabel={(option) => `${option.name} ${option.displayName}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Course"
              placeholder="Start typing to search courses..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paperDark',
                },
              }}
            />
          )}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleRestartCourse}
          disabled={!selectedCourse || loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? <CircularProgress size={20} /> : 'Restart Course'}
        </Button>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
